// src/pages/RecordsPage.jsx
import React, { useState, useMemo } from 'react';

function RecordsPage() {
  const [activeTab, setActiveTab] = useState('inventory'); // 'inventory' or 'transactions'
  const [searchTerm, setSearchTerm] = useState('');

  // Filtered records based on search
  const filteredRecords = useMemo(() => {
    // Sample data
    const inventoryRecords = [
      { date: '2024-06-15 14:32', action: 'Added', item: 'Engine Oil Filter', user: 'Admin User', details: 'Added 100 units at ₱50 each' },
      { date: '2024-06-15 13:20', action: 'Edited', item: 'Brake Pads Set', user: 'Admin User', details: 'Updated price from ₱95 to ₱100' },
      { date: '2024-06-15 11:45', action: 'Deleted', item: 'Old Spark Plug Model', user: 'Admin User', details: 'Removed discontinued item' },
      { date: '2024-06-14 16:15', action: 'Edited', item: 'Chain Sprocket Kit', user: 'Admin User', details: 'Updated stock from 150 to 180' },
      { date: '2024-06-14 10:30', action: 'Added', item: 'Air Filter', user: 'Admin User', details: 'Added 200 units at ₱50 each' },
      { date: '2024-06-13 15:45', action: 'Edited', item: 'Clutch Cable', user: 'Admin User', details: 'Updated low stock threshold from 15 to 20' },
      { date: '2024-06-13 09:20', action: 'Added', item: 'Rear View Mirror', user: 'Admin User', details: 'Added 50 units at ₱45 each' },
      { date: '2024-06-12 14:10', action: 'Deleted', item: 'Damaged Battery', user: 'Admin User', details: 'Removed defective item' },
    ];

    const transactionRecords = [
      { date: '2024-06-15 14:30', id: 'TXN-2024-001', customer: 'Juan Dela Cruz', amount: '₱220', status: 'Completed' },
      { date: '2024-06-15 13:15', id: 'TXN-2024-002', customer: 'Maria Santos', amount: '₱100', status: 'Completed' },
      { date: '2024-06-14 16:45', id: 'TXN-2024-003', customer: 'Pedro Garcia', amount: '₱200', status: 'Completed' },
      { date: '2024-06-14 11:20', id: 'TXN-2024-004', customer: 'Ana Reyes', amount: '₱50', status: 'Completed' },
      { date: '2024-06-13 15:30', id: 'TXN-2024-005', customer: 'Carlos Mendoza', amount: '₱250', status: 'Completed' },
      { date: '2024-06-13 10:15', id: 'TXN-2024-006', customer: 'Rosa Torres', amount: '₱600', status: 'Completed' },
      { date: '2024-06-12 14:45', id: 'TXN-2024-007', customer: 'Luis Ramos', amount: '₱125', status: 'Completed' },
      { date: '2024-06-12 09:30', id: 'TXN-2024-008', customer: 'Elena Cruz', amount: '₱120', status: 'Pending' },
    ];

    const data = activeTab === 'inventory' ? inventoryRecords : transactionRecords;
    if (!searchTerm) return data;

    const lowerSearch = searchTerm.toLowerCase();
    return data.filter(record => {
      if (activeTab === 'inventory') {
        return (
          record.item.toLowerCase().includes(lowerSearch) ||
          record.action.toLowerCase().includes(lowerSearch) ||
          record.details.toLowerCase().includes(lowerSearch)
        );
      } else {
        return (
          record.id.toLowerCase().includes(lowerSearch) ||
          record.customer.toLowerCase().includes(lowerSearch) ||
          record.amount.includes(searchTerm)
        );
      }
    });
  }, [activeTab, searchTerm]);

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'Added': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400';
      case 'Edited': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400';
      case 'Deleted': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getStatusBadgeColor = (status) => {
    return status === 'Completed'
      ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400'
      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400';
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Records</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          View history of inventory changes and transactions
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => { setActiveTab('inventory'); setSearchTerm(''); }}
            className={`flex-1 px-6 py-3 text-sm font-medium transition ${
              activeTab === 'inventory'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Inventory History
          </button>
          <button
            onClick={() => { setActiveTab('transactions'); setSearchTerm(''); }}
            className={`flex-1 px-6 py-3 text-sm font-medium transition ${
              activeTab === 'transactions'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Transaction History
          </button>
        </div>
      </div>

      {/* Header with Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {activeTab === 'inventory' ? 'Inventory Changes' : 'Transaction History'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-md w-full">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${activeTab === 'inventory' ? 'inventory' : 'transaction'} history...`}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            />
            <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date & Time</th>
                {activeTab === 'inventory' ? (
                  <>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Item</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Details</th>
                  </>
                ) : (
                  <>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Transaction ID</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRecords.map((record, idx) => (
                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{record.date}</td>
                  {activeTab === 'inventory' ? (
                    <>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getActionBadgeColor(record.action)}`}>
                          {record.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{record.item}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{record.user}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{record.details}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{record.id}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{record.customer}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{record.amount}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(record.status)}`}>
                          {record.status}
                        </span>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {filteredRecords.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No records found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RecordsPage;